/**
 * Format a number as currency in AFN
 * @param amount Amount to format as currency
 */
export const formatCurrency = (amount: number): string => {
  return amount.toLocaleString();
};

/**
 * Format a date string or object to a human-readable format
 * @param date Date to format
 */
export const formatDate = (date: string | Date): string => {
  if (!date) return 'N/A';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return 'Invalid date';
  return dateObj.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
};

/**
 * Format a number as a percentage
 * @param value Value to format as percentage 
 */
export const formatPercentage = (value: number): string => {
  return `${value.toFixed(1)}%`;
};

/**
 * Format a phone number
 * @param phone Phone number to format
 */
export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return '';
  
  // Remove non-numeric characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Format based on length
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length === 11) {
    return `+${cleaned.slice(0, 1)} (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  
  // Default: return original value
  return phone;
};

/**
 * Format a file size in bytes to human-readable format
 * @param bytes Number of bytes
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Format a value based on its type or special handling
 * @param value Value to format
 * @param isCurrency Whether to format as currency
 */
export const formatValue = (value: string | number, isCurrency = false): string => {
  if (typeof value === 'undefined' || value === null) {
    return 'N/A';
  }
  
  if (isCurrency) {
    return formatCurrency(typeof value === 'string' ? parseFloat(value) : value);
  }
  
  if (typeof value === 'number') {
    return value.toLocaleString();
  }
  
  return value;
};

/**
 * Convert 24-hour time format to 12-hour format
 * @param time Time in 24-hour format (HH:MM)
 */
export const to12HourFormat = (time: string): string => {
  if (!time) return '';
  
  try {
    // Parse the time string (assuming format is HH:MM or HH:MM:SS)
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    
    if (isNaN(hour)) return time; // Return original if parsing fails
    
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12; // Convert 0 to 12
    
    return `${hour12}:${minutes} ${period}`;
  } catch (error) {
    console.error('Error formatting time:', error);
    return time; // Return original if error occurs
  }
};
